/**
 * 星空底:自现有官网 src/components/home/StarField.tsx 移植,
 * 只保留登录弹窗与个人中心用到的「深色 backdrop」一档——
 * 银河带星尘 + 柔雾星云 + 已知星群(带星表环)+ 深空群星 + CSS 流星。
 * 去掉了灯照暗星、知识图谱连线与指针视差(那几条只在首屏交互里用)。
 */
import {useEffect, useRef} from 'react';

type Star = {x: number; y: number; r: number; a: number; tw: number; ts: number; ring: boolean};

const SEED = 20260827;
const STAR: [number, number, number] = [250, 250, 250];
const ACCENT: [number, number, number] = [169, 155, 199];
const NEBULA_ALPHA = 0.055;

/** 流星:位置与节奏固定错开,纯合成器动画零 JS 开销。 */
const METEORS = [
 {left: '68%', top: '-4%', delay: '0s', dur: '7s'},
 {left: '28%', top: '-6%', delay: '2.4s', dur: '9s'},
 {left: '86%', top: '6%', delay: '4.8s', dur: '8s'},
 {left: '46%', top: '-2%', delay: '7.5s', dur: '10s'},
 {left: '12%', top: '3%', delay: '10s', dur: '11s'},
 {left: '58%', top: '10%', delay: '13.5s', dur: '12s'},
];

function mulberry32(seed: number): () => number {
 let a = seed;
 return () => {
  a |= 0;
  a = (a + 0x6d2b79f5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
 };
}

function makeStars(rnd: () => number, n: number, rMin: number, rMax: number): Star[] {
 return Array.from({length: n}, () => ({
  x: rnd(),
  y: rnd(),
  r: rMin + rnd() * (rMax - rMin),
  a: 0.35 + rnd() * 0.65,
  tw: rnd() * Math.PI * 2,
  ts: 0.0014 + rnd() * 0.0026, // 闪烁周期 1.5~4s:像真的星星
  ring: rnd() > 0.94, // 少数大星带星表环(天文手稿味)
 }));
}

/** 真实闪烁曲线:大部分时间偏暗,周期性地「眨」一下。 */
function twinkleOf(now: number, s: Star): number {
 const base = 0.5 + 0.5 * Math.sin(now * s.ts + s.tw);
 return 0.28 + 0.72 * Math.pow(base, 2.2);
}

/** 微漂移:星星缓慢游动,不是死的。 */
const wobX = (now: number, s: Star): number => Math.sin(now * 0.00006 + s.tw * 3) * 3;
const wobY = (now: number, s: Star): number => Math.cos(now * 0.00005 + s.tw * 2) * 2;

/** 银河带轴线:左下 → 右上的对角,t∈[0,1]。 */
const bandPoint = (t: number): [number, number] => [0.05 + 0.9 * t, 0.78 - 0.62 * t];
const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));
const rgba = (c: [number, number, number], a: number): string => `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${a})`;

/** 登录 / 个人中心的星空底。整块 aria-hidden,纯装饰。 */
export function AuthStarField() {
 const canvasRef = useRef<HTMLCanvasElement>(null);

 useEffect(() => {
  const canvas = canvasRef.current;
  const ctx = canvas?.getContext('2d');
  if (!canvas || !ctx) return;

  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const rnd = mulberry32(SEED);
  const lit = makeStars(rnd, 90, 0.7, 1.9);
  const deeps = makeStars(rnd, 110, 0.4, 1.0);
  const bandStars: Star[] = Array.from({length: 300}, () => {
   const t = rnd();
   const off = (rnd() + rnd() + rnd() - 1.5) * 0.15;
   const [bx, by] = bandPoint(t);
   return {
    x: bx + 0.5547 * off,
    y: by + 0.8321 * off,
    r: 0.3 + rnd() * 0.7,
    a: 0.3 + rnd() * 0.7,
    tw: rnd() * Math.PI * 2,
    ts: 0.0014 + rnd() * 0.0026,
    ring: false,
   };
  });
  const nebulae = Array.from({length: 6}, (_, i) => {
   const [bx, by] = bandPoint((i + 0.5) / 6);
   return {x: bx + (rnd() - 0.5) * 0.05, y: by + (rnd() - 0.5) * 0.05, r: 0.15 + rnd() * 0.1};
  });

  const dpr = Math.min(1.5, window.devicePixelRatio || 1);
  let w = 0;
  let h = 0;
  const resize = () => {
   w = canvas.offsetWidth;
   h = canvas.offsetHeight;
   canvas.width = Math.max(2, Math.round(w * dpr));
   canvas.height = Math.max(2, Math.round(h * dpr));
   ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();

  let inView = true;
  let awakeAt = -1;

  /** 微星快速路径:亚像素尺寸用 fillRect 代替 arc 路径(视觉无差,省路径机开销)。 */
  const dot = (x: number, y: number, r: number) => {
   if (r <= 1.2) {
    ctx.fillRect(x - r, y - r, r + r, r + r);
    return;
   }
   ctx.beginPath();
   ctx.arc(x, y, r, 0, Math.PI * 2);
   ctx.fill();
  };

  // 银河雾带离屏缓存:6 个径向渐变只在尺寸/亮度档变化时重烘,逐帧只 drawImage
  const nebCanvas = document.createElement('canvas');
  const nebCtx = nebCanvas.getContext('2d');
  let nebKey = '';

  let raf = 0;
  const tick = (now: number) => {
   raf = 0;
   if (!inView || document.hidden || w < 4) return;
   if (awakeAt < 0) awakeAt = now;
   const sinceAwake = still ? 3000 : now - awakeAt;
   const boost = 1 + 0.3 * clamp01(sinceAwake / 1500);
   const starColor = rgba(STAR, 1);

   ctx.clearRect(0, 0, w, h);

   const boostQ = Math.round(boost * 24);
   const bakeKey = `${canvas.width}x${canvas.height}|${boostQ}`;
   if (nebCtx && bakeKey !== nebKey) {
    nebKey = bakeKey;
    nebCanvas.width = canvas.width;
    nebCanvas.height = canvas.height;
    nebCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    nebCtx.clearRect(0, 0, w, h);
    const bakedBoost = boostQ / 24;
    for (const n of nebulae) {
     const nx = n.x * w;
     const ny = n.y * h;
     const R = n.r * Math.max(w, h);
     const g = nebCtx.createRadialGradient(nx, ny, 0, nx, ny, R);
     g.addColorStop(0, rgba(ACCENT, NEBULA_ALPHA * bakedBoost));
     g.addColorStop(1, rgba(ACCENT, 0));
     nebCtx.fillStyle = g;
     nebCtx.fillRect(nx - R, ny - R, R * 2, R * 2);
    }
   }
   if (nebCtx) ctx.drawImage(nebCanvas, 0, 0, nebCanvas.width, nebCanvas.height, 0, 0, w, h);

   // 银河微星:沿带密布的星尘
   ctx.fillStyle = starColor;
   for (const s of bandStars) {
    ctx.globalAlpha = 0.3 * s.a * twinkleOf(now, s) * boost;
    dot(s.x * w + wobX(now, s), s.y * h + wobY(now, s), s.r);
   }

   // 已知星群:真实闪烁 + 微漂移,少数带星表环
   for (const s of lit) {
    const x = s.x * w + wobX(now, s);
    const y = s.y * h + wobY(now, s);
    const twinkle = twinkleOf(now, s);
    const alpha = 0.48 * s.a * twinkle * boost;
    ctx.fillStyle = s.ring ? rgba(ACCENT, 1) : starColor;
    ctx.globalAlpha = s.ring ? Math.min(1, alpha * 1.5) : alpha;
    ctx.beginPath();
    ctx.arc(x, y, s.r * (0.85 + 0.3 * twinkle), 0, Math.PI * 2);
    ctx.fill();
    if (s.ring) {
     ctx.globalAlpha = 1;
     ctx.strokeStyle = rgba(ACCENT, alpha * 0.8);
     ctx.beginPath();
     ctx.arc(x, y, s.r + 3.5, 0, Math.PI * 2);
     ctx.stroke();
    }
   }

   // 深空群星:苏醒之后,浩瀚显现
   if (sinceAwake > 400) {
    const f = clamp01((sinceAwake - 400) / 2000);
    ctx.fillStyle = starColor;
    for (const s of deeps) {
     ctx.globalAlpha = 0.26 * s.a * f * twinkleOf(now, s);
     dot(s.x * w + wobX(now, s), s.y * h + wobY(now, s), s.r);
    }
   }

   ctx.globalAlpha = 1;
   if (!still) raf = requestAnimationFrame(tick);
  };

  const syncVisibility = () => {
   cancelAnimationFrame(raf);
   raf = 0;
   if (inView && !document.hidden) raf = requestAnimationFrame(tick);
  };
  const ro = new ResizeObserver(() => {
   resize();
   syncVisibility();
  });
  ro.observe(canvas);
  const io = new IntersectionObserver(entries => {
   inView = entries[0]?.isIntersecting ?? true;
   syncVisibility();
  });
  io.observe(canvas);
  document.addEventListener('visibilitychange', syncVisibility);
  syncVisibility();

  return () => {
   cancelAnimationFrame(raf);
   ro.disconnect();
   io.disconnect();
   document.removeEventListener('visibilitychange', syncVisibility);
  };
 }, []);

 return (
  <div className="auth-stars" aria-hidden="true">
   <canvas ref={canvasRef} />
   {METEORS.map(m => (
    <span
     key={`${m.left}-${m.delay}`}
     className="auth-meteor"
     style={{left: m.left, top: m.top, ['--delay' as string]: m.delay, ['--dur' as string]: m.dur}}
    />
   ))}
  </div>
 );
}
